// Feature: docs/features/org-chat-explorer.md
import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Spinner,
  SimpleGrid,
  VStack,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import ModernChat from './ModernChat';

const OrgChatExplorer = () => {
  const { accessToken } = useContext(AppContext);
  
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChats = async () => {
      if (!accessToken) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await callApi('/org-chats', accessToken, { method: 'GET' });
        
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success') {
            setChats(result.data);
          } else {
            setError(result.message || 'Failed to fetch chat history');
          }
        } else {
          if (response.status === 403) {
            setError("Forbidden: You do not have permission to view organization chat history.");
          } else {
            setError(`API Error: ${response.status}`);
          }
        }
      } catch (err) {
        console.error("Error fetching org chats:", err);
        setError('An error occurred while fetching data.');
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [accessToken]);

  // Compile the chat transcripts into a single text block for the AI
  const compiledTranscripts = useMemo(() => {
    if (!chats || chats.length === 0) return null;

    return chats.map(chat => {
      const header = `--- Chat Session: ${chat.name} (Context: ${chat.context_type}) | User: ${chat.user_name} ---`;
      const messages = chat.messages.map(msg => `${msg.actor.toUpperCase()}: ${msg.message}`).join('\n\n');
      return `${header}\n${messages}`;
    }).join('\n\n\n');
  }, [chats]);

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg" color="gray.800">Organization Chat Explorer</Heading>
            <Text color="gray.500" mt={1}>Review and summarize AI chat interactions across your organization over the last 7 days.</Text>
          </Box>
        </Flex>

        {error && (
          <Box bg="red.100" color="red.700" p={4} borderRadius="md" mb={6}>
            {error}
          </Box>
        )}

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : !error && (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {/* Left Column: AI Summary Chat */}
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" height="700px" display="flex" flexDirection="column">
              <Heading size="sm" mb={4} color="gray.600">AI Usage Analyst</Heading>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Ask the AI to summarize how your team is using the chat assistants. Try: "What are the most common questions being asked?"
              </Text>
              <Box flex="1" overflow="hidden">
                {compiledTranscripts ? (
                  <ModernChat 
                    caseId="dashboard-summary"
                    contextType="org_chat_summary"
                    additionalContext={compiledTranscripts}
                    height="100%"
                  />
                ) : (
                  <Flex justify="center" align="center" height="100%">
                    <Text color="gray.500">No chat history available to summarize.</Text>
                  </Flex>
                )}
              </Box>
            </Box>

            {/* Right Column: Raw Transcripts */}
            <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden" height="700px" display="flex" flexDirection="column">
              <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200" bg="gray.50">
                <Heading size="sm" color="gray.800">Raw Transcripts ({chats.length})</Heading>
              </Box>
              <Box overflowY="auto" flex="1" p={4}>
                {chats.length === 0 ? (
                  <Text color="gray.500" textAlign="center" mt={10}>No chats found in the last 7 days.</Text>
                ) : (
                  <Accordion allowMultiple>
                    {chats.map((chat, idx) => (
                      <AccordionItem key={idx} mb={2} borderWidth="1px" borderRadius="md" borderColor="gray.200">
                        <h2>
                          <AccordionButton _expanded={{ bg: 'blue.50' }}>
                            <Box flex="1" textAlign="left">
                              <Text fontWeight="bold" fontSize="sm">{chat.name}</Text>
                              <Flex gap={3} mt={1} align="center">
                                <Text fontSize="xs" color="gray.500">{chat.created_at.split('T')[0]}</Text>
                                <Text fontSize="xs" color="gray.500">User: {chat.user_name}</Text>
                                <Badge colorScheme="blue" fontSize="2xs">{chat.context_type}</Badge>
                              </Flex>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4} bg="white">
                          <VStack align="stretch" spacing={4}>
                            {chat.messages.map((msg, mIdx) => (
                              <Box 
                                key={mIdx} 
                                p={3} 
                                borderRadius="md" 
                                bg={msg.actor === 'user' ? 'gray.100' : 'blue.50'}
                                alignSelf={msg.actor === 'user' ? 'flex-end' : 'flex-start'}
                                maxW="90%"
                              >
                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                                  {msg.actor.toUpperCase()}
                                </Text>
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                  {msg.message}
                                </Text>
                              </Box>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </Box>
            </Box>
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default OrgChatExplorer;